import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useSearchParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider, useTheme } from "./context/ThemeContext";
import { ProjectProvider, useProject } from "./context/ProjectContext";
import { Toaster } from "./components/ui/sonner";
import Login from "./pages/Login";
import SetPassword from "./pages/SetPassword";
import Onboarding from "./pages/Onboarding";
import AppLayout from "./components/AppLayout";
import Dashboard from "./pages/Dashboard";
import Projects from "./pages/Projects";
import NewProject from "./pages/NewProject";
import ProjectDetail from "./pages/ProjectDetail";
import Clients from "./pages/Clients";
import Scm from "./pages/Scm";
import ScmInquiry from "./pages/ScmInquiry";
import ClientDetail from "./pages/ClientDetail";
import IndustryPacks from "./pages/IndustryPacks";
import PackWizard from "./pages/PackWizard";
import Analytics from "./pages/Analytics";
import Portal from "./pages/Portal";
import Bom from "./pages/Bom";
import Admin from "./pages/Admin";
import Pricing from "./pages/Pricing";
import EquipmentCatalog from "./pages/EquipmentCatalog";
import Symbology from "./pages/Symbology";
import ModelEngine from "./pages/ModelEngine";

function FullScreenLoader() {
  return (
    <div className="min-h-screen bg-[var(--c-bg)] flex items-center justify-center" data-testid="app-loading">
      <div className="font-mono text-xs tracking-widest text-[var(--c-muted)]">FORMIGEN // LOADING</div>
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <FullScreenLoader />;
  if (user === false) return <Navigate to="/login" replace />;
  const done = user.org?.onboarding_completed;
  if (!done && loc.pathname !== "/onboarding") return <Navigate to="/onboarding" replace />;
  if (done && loc.pathname === "/onboarding") return <Navigate to="/dashboard" replace />;
  if (user.must_change_password && loc.pathname !== "/set-password") return <Navigate to="/set-password" replace />;
  return children;
}

const ROUTE_MODULES = {
  "/design-engine": "design", "/validation": "validation", "/drawing-studio": "drawings",
  "/documents": "documents", "/bom": "bom", "/scm": "scm", "/analytics": "analytics",
};

function ModuleGuard({ children }) {
  const { user } = useAuth();
  const loc = useLocation();
  if (user === null) return <FullScreenLoader />;
  const base = "/" + loc.pathname.split("/")[1];
  const mod = ROUTE_MODULES[base];
  const mods = user?.modules; // undefined -> fail open
  if (mod && user?.role !== "admin" && mods && !mods.includes(mod)) {
    return (
      <div className="flex-1 flex items-center justify-center p-10" data-testid="module-access-denied">
        <div className="text-center max-w-sm">
          <div className="text-sm font-medium text-[var(--c-text)]">No access to this module</div>
          <div className="text-xs text-[var(--c-muted)] mt-2 leading-relaxed">
            Your workspace membership doesn't include this module. Contact your admin to grant access under Admin → Workspaces.
          </div>
          <a href="/dashboard" data-testid="access-denied-back"
            className="inline-block mt-4 px-4 py-2 bg-[var(--c-accent)] hover:bg-[var(--c-accent)]/85 text-[var(--c-text)] text-xs rounded-sm transition-colors duration-150">
            Back to Dashboard
          </a>
        </div>
      </div>
    );
  }
  return children;
}

// Legacy module URLs now resolve to the single project workspace.
function ModuleRedirect({ tab }) {
  const { pid, loading } = useProject();
  const [params] = useSearchParams();
  const target = params.get("project") || params.get("project_id") || pid;
  if (loading && !target) return <FullScreenLoader />;
  return <Navigate to={target ? `/projects/${target}?tab=${tab}` : "/projects"} replace />;
}

function LoginRoute() {  const { user } = useAuth();
  if (user === null) return <FullScreenLoader />;
  if (user) return <Navigate to={user.must_change_password ? "/set-password" : (user.org?.onboarding_completed ? "/dashboard" : "/onboarding")} replace />;
  return <Login />;
}

function ThemedToaster() {
  const { resolved } = useTheme();
  return (
    <Toaster theme={resolved} position="top-right"
      toastOptions={{ style: { background: "var(--c-surface)", border: "1px solid var(--c-border)", color: "var(--c-text)" } }} />
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginRoute />} />
            <Route path="/portal/:pid/:token" element={<Portal />} />
            <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
            <Route path="/set-password" element={<Protected><SetPassword /></Protected>} />
            <Route element={<Protected><ProjectProvider><AppLayout /></ProjectProvider></Protected>}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/projects" element={<Projects />} />
              <Route path="/projects/new" element={<NewProject />} />
              <Route path="/projects/:id" element={<ProjectDetail />} />
              <Route path="/clients" element={<Clients />} />
              <Route path="/clients/:id" element={<ClientDetail />} />
              <Route path="/design-engine" element={<ModuleRedirect tab="design" />} />
              <Route path="/validation" element={<ModuleRedirect tab="validation" />} />
              <Route path="/drawing-studio" element={<ModuleRedirect tab="drawings" />} />
              <Route path="/documents" element={<ModuleRedirect tab="documents" />} />
              <Route path="/bom" element={<ModuleRedirect tab="bom" />} />
              <Route path="/bom-dashboard" element={<ModuleGuard><Bom /></ModuleGuard>} />
              <Route path="/scm" element={<ModuleGuard><Scm /></ModuleGuard>} />
              <Route path="/scm/inquiries/:id" element={<ModuleGuard><ScmInquiry /></ModuleGuard>} />
              <Route path="/client-rules" element={<Navigate to="/admin?tab=client-rules" replace />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/equipment" element={<EquipmentCatalog />} />
              <Route path="/symbology" element={<Symbology />} />
              <Route path="/model-engine" element={<ModelEngine />} />
              <Route path="/industry-packs" element={<IndustryPacks />} />
              <Route path="/industry-packs/new" element={<PackWizard />} />
              <Route path="/industry-packs/:key/edit" element={<PackWizard />} />
              <Route path="/settings" element={<Navigate to="/admin?tab=settings" replace />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
            </Route>
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
        <ThemedToaster />
        </AuthProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
